import {
	createVariantChangeEvent,
	publish,
	subscribe,
	isVariantChangeEvent,
	PUB_SUB_EVENTS,
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type ProductForm } from '@/scripts/product/product-form'
import { type MediaGallery } from '@/scripts/product/media-gallery'
import { type ShareButton } from '@/scripts/optional/share-button'
import { type PickupAvailability } from '@/scripts/optional/pickup-availability'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type ProductVariant } from '@/scripts/shopify'
import type { KlaviyoWaitlistInline } from '@/scripts/product/klaviyo-waitlist-inline'

export class VariantSelects extends UcoastEl {
	static htmlSelector = 'variant-selects'
	static selectors = {}
	instanceSelectors = VariantSelects.selectors
	currentVariant?: ProductVariant
	options?: string[]
	variantData?: ProductVariant[]
	variantChangeUnsubscriber?: () => void
	cartUpdateUnsubscriber?:() => void
	private isSyncing = false

	constructor() {
		super()
		this.setInstanceSelectors()
		this.addEventListener('change', this.onVariantChange)
	}

	override connectedCallback() {
		super.connectedCallback()
		// Subscribe to variant changes from other pickers
		this.variantChangeUnsubscriber = subscribe(
			PUB_SUB_EVENTS.variantChange,
			(event) => {
				if (!isVariantChangeEvent(event)) return
				// Only sync if the event is from the same section but different picker
				const sectionId =
					this.dataset.originalSection ?? this.dataset.section
				if (event.data.sectionId !== sectionId) return
				// Prevent infinite loops - don't sync if we triggered the change
				if (this.isSyncing) return
				this.syncVariantSelection(event.data.variant)
			}
		)
		this.cartUpdateUnsubscriber = subscribe(
			PUB_SUB_EVENTS.cartUpdate,
			() => {
				this.updateSubscriptionButtonState()
			}
		)

		// Check initial variant availability on page load
		this.updateOptions()
		this.updateMasterId()
		this.updateSubscriptionButtonState()
		if (this.currentVariant) {
			this.updateSoldoutState(!this.currentVariant.available)
		}
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		if (this.variantChangeUnsubscriber) {
			this.variantChangeUnsubscriber()
		}
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber()
		}
	}

	setInstanceSelectors() {
		this.instanceSelectors = VariantSelects.selectors
	}

	// Sync select values to match the variant selected in another picker
	syncVariantSelection(variant: ProductVariant) {
		if (!variant || !variant.options) return
		const selects = q.ol<HTMLSelectElement>('select', this)
		if (!selects) return

		selects.forEach((select, index) => {
			const optionValue = variant.options[index]
			if (optionValue && select.value !== optionValue) {
				select.value = optionValue
			}
		})

		// Update internal state without triggering another change event
		this.currentVariant = variant
		this.updateOptions()
	}

	onVariantChange() {
		// Quick work around, can use some improvements
		const isSculpt9 = window.meta?.product?.handle === 'sculpt-9';
		if (isSculpt9) {
			this.sculpt9VariantChange()
			return
		};
		this.isSyncing = true // Prevent sync loops
		this.updateOptions()
		this.updateMasterId()
		this.toggleAddButton(true, '', false)
		this.updatePickupAvailability()
		this.removeErrorMessage()
		this.updateVariantStatuses()

		if (!this.currentVariant) {
			this.toggleAddButton(true, '', true)
			this.setUnavailable()
			this.updateSoldoutState(true)
			this.isSyncing = false
		} else {
			// Update soldout state based on variant availability
			this.updateSoldoutState(!this.currentVariant.available)
			this.updateMedia()
			this.updateURL()
			this.updateVariantInput()
			this.renderProductInfo()
			this.updateShareUrl()
			// Note: isSyncing is reset after renderProductInfo publishes the event
		}
	}

	sculpt9VariantChange() {
		this.updateOptions()
		this.updateMasterId()
		this.updateVariantInput()

		const sku = this.currentVariant?.sku
		if (!sku) return;
		const skuParts = sku.split('-');
		const productForm = q.rs<HTMLFormElement>(
			`#product-form-sticky-${this.dataset.section}`
		)
		const qtyInput = q.os<HTMLInputElement>(
			'input[name="quantity"]',
			productForm
		)
		if (!qtyInput) return
		const _qty = Number.parseInt(skuParts[1] ?? '1', 10)
		const qty = skuParts.length > 1 ? _qty : 1;
		qtyInput.value = qty.toString()

		this.setSculpt9Prices()

		const subscriptionWidget = document.querySelector<HTMLDivElement>('.sculpt-subscription-widget')
		if (!subscriptionWidget) return
		const subscriptionOptions = subscriptionWidget.querySelectorAll<HTMLInputElement>('input[name="subscription-option"]')
		if (subscriptionOptions.length === 0) return
		const selectedValue = this.currentVariant?.title.toLowerCase().replace(' ', '-').replace(/s$/, '')
		const selectedOption = Array.from(subscriptionOptions).find((option) => option.value === selectedValue)
		if (!selectedOption) return
		selectedOption.checked = true
		selectedOption.dispatchEvent(new Event('click'))
	}

	setSculpt9Prices() {
		const sellingPlanAllocation = this.currentVariant?.selling_plan_allocations?.at(-1)
		const price = sellingPlanAllocation?.price ?? this.currentVariant?.price ?? 0
		const compareAtPrice = sellingPlanAllocation?.compare_at_price ?? this.currentVariant?.price ?? 0
		const isDiscounted = compareAtPrice > price
		const stickyAtc = document.querySelector<HTMLDivElement>('.product__sticky-atc')!
		const priceContainer = stickyAtc.querySelector<HTMLDivElement>('.price__container')!
		const priceRegular = priceContainer.querySelector<HTMLDivElement>('.price__regular')!
		const priceSale = priceContainer.querySelector<HTMLDivElement>('.price__sale')!

		if (isDiscounted) {
			priceContainer.parentElement?.classList.add('price--on-sale')
			priceSale.querySelector<HTMLSpanElement>('span.price-item')!.textContent = `$${price / 100}`
			priceSale.querySelector<HTMLSpanElement>('s.price-item')!.textContent = `$${compareAtPrice / 100}`
		} else {
			priceContainer.parentElement?.classList.remove('price--on-sale')
			priceRegular.querySelector<HTMLSpanElement>('span.price-item')!.textContent = `$${price / 100}`
		}
	}

	updateSoldoutState(isOutOfStock: boolean) {
		const soldoutElement =
			document.querySelector<HTMLElement>('.product__soldout')
		const buyButtonsWrapper =
			document.querySelector<HTMLElement>('.add__card-wrapper')

		// If the server already rendered the element as OOS and JS is trying
		// to hide it (isOutOfStock=false), only allow that once the user has
		// interacted with a variant picker (i.e. data-oos-initial is removed).
		// This prevents mount-time JS from overriding the server-rendered state.
		if (!isOutOfStock && soldoutElement?.hasAttribute('data-oos-initial')) {
			return
		}

		// Clear the initial flag on any legitimate state change
		soldoutElement?.removeAttribute('data-oos-initial')

		if (soldoutElement) {
			soldoutElement.classList.toggle('active', isOutOfStock)
			soldoutElement.style.display = isOutOfStock ? 'block' : 'none'
		}

		// Show/hide buy buttons wrapper to match soldout state
		if (buyButtonsWrapper) {
			buyButtonsWrapper.classList.toggle('hidden', isOutOfStock)
			buyButtonsWrapper.style.display = isOutOfStock ? 'none' : ''
		}

		// Keep waitlist form variant in sync with the selected variant
		if (isOutOfStock && this.currentVariant) {
			const waitlistForm =
				document.querySelector<KlaviyoWaitlistInline>('klaviyo-waitlist-inline')
			if (waitlistForm) {
				waitlistForm.updateVariant(
					`${this.currentVariant.id}`,
					this.currentVariant.title ?? ''
				)
			}
		}
	}

	updateOptions() {
		this.options = Array.from(
			q.rl<HTMLSelectElement>('select', this),
			(select) => select.value
		)
	}

	updateMasterId() {
		this.currentVariant = this.getVariantData().find((variant) => {
			return !variant.options
				.map((option, index) => {
					return this.options && this.options[index] === option
				})
				.includes(false)
		})
	}

	updateMedia() {
		const currentVariant = this.currentVariant
		if (!currentVariant) return
		if (!currentVariant.featured_media) return

		const mediaGalleries = q.ol<MediaGallery>(
			`[id^="MediaGallery-${this.dataset.section}"]`
		)
		mediaGalleries?.forEach((mediaGallery) =>
			mediaGallery.setActiveMedia(
				`${this.dataset.section}-${currentVariant.featured_media.id}`,
				true
			)
		)

		const modalContent = q.os(
			`#ProductModal-${this.dataset.section} .product-media-modal__content`
		)
		if (modalContent) {
			const newMediaModal = q.os(
				`[data-media-id="${currentVariant.featured_media.id}"]`,
				modalContent
			)
			if (newMediaModal) modalContent.prepend(newMediaModal)
		}

		// Update sticky ATC thumbnail
		this.updateStickyThumbnail(currentVariant.featured_media.id)
	}

	updateStickyThumbnail(mediaId: number) {
		const stickyThumbnail = document.querySelector<HTMLElement>(
			`[data-sticky-thumbnail="${this.dataset.section}"]`
		)
		if (!stickyThumbnail) return

		// Hide all variant thumbnails and fallback
		stickyThumbnail
			.querySelectorAll<HTMLElement>('.sticky-thumbnail__variant, .sticky-thumbnail__fallback')
			.forEach((el) => {
				el.classList.remove('is-active')
				el.style.display = 'none'
			})

		// Show the matching variant thumbnail
		const target = stickyThumbnail.querySelector<HTMLElement>(
			`[data-sticky-media-id="${mediaId}"]`
		)
		if (target) {
			target.classList.add('is-active')
			target.style.display = ''
		} else {
			// Show fallback if no matching variant thumbnail
			const fallback = stickyThumbnail.querySelector<HTMLElement>(
				'.sticky-thumbnail__fallback'
			)
			if (fallback) {
				fallback.classList.add('is-active')
				fallback.style.display = ''
			}
		}
	}

	updateURL() {
		if (!this.currentVariant || this.dataset.updateUrl === 'false') return
		window.history.replaceState(
			{},
			'',
			`${this.dataset.url}?variant=${this.currentVariant.id}`
		)
	}

	updateShareUrl() {
		if (!this.currentVariant)
			throw new Error('cannot updateShareUrl without currentVariant')
		const shareButton = q.os<ShareButton>(`#Share-${this.dataset.section}`)
		if (!shareButton || !shareButton.updateUrl) return
		shareButton.updateUrl(
			`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`
		)
	}

	updateVariantInput() {
		const currentVariant = this.currentVariant
		if (!currentVariant)
			throw new Error('cannot updateVariantInput without currentVariant')
		const productForms = q.ol(
			`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-sticky-${this.dataset.section}`
		)
		productForms?.forEach((productForm) => {
			const input = q.rs<HTMLInputElement>(
				'input[name="id"]',
				productForm
			)
			input.value = `${currentVariant.id}`
			input.dispatchEvent(new Event('change', { bubbles: true }))

			// Update selling plan input if variant requires one
			const sellingPlanInput = q.os<HTMLInputElement>(
				'input[name="selling_plan"]',
				productForm
			)

			const isSculpt9 = window.meta?.product?.handle === 'sculpt-9';
			const shouldProcessSellingAllocations =
				currentVariant.requires_selling_plan && currentVariant.selling_plan_allocations?.length > 0
				|| isSculpt9 && currentVariant.selling_plan_allocations?.length > 0

			if (sellingPlanInput) {
				if (shouldProcessSellingAllocations) {
					// Use the variant's last allocation. `[0]` is the shared
					// 1-month-recurring plan that every Sculpt 9 variant carries,
					// which produces a "qty=N on a 1-month-recurring line" cart
					// state on multi-month variants. The variant-specific plan
					// (the prepaid one for that duration) lives at the end of
					// the allocations array. Matches the convention already used
					// in setSculpt9Prices above.
					const sellingPlanAllocation = currentVariant.selling_plan_allocations.at(-1)
					const sellingPlanId = sellingPlanAllocation?.selling_plan?.id || (sellingPlanAllocation as any)?.selling_plan_id
					sellingPlanInput.value = `${sellingPlanId}`
				} else {
					sellingPlanInput.value = ''
				}
			}
		})
	}

	getVariantOptionFromIndex(variant: ProductVariant, index: number) {
		switch (index) {
			case 1:
				return variant.option1
			case 2:
				return variant.option2
			case 3:
				return variant.option3
			default:
				throw new Error('invalid index')
		}
	}

	updateVariantStatuses() {
		if (!this.variantData)
			throw new Error('cannot updateVariantStatuses without variantData')
		const selectedOptionOneVariants = this.variantData.filter(
			(variant) =>
				q.rs<HTMLInputElement | HTMLOptionElement>(':checked', this)
					.value === variant.option1
		)
		const inputWrapperNodes = q.ol('.product-form__input', this)
		const inputWrappers = inputWrapperNodes?.length
			? Array.from(inputWrapperNodes)
			: []
		if (inputWrappers.length) {
			inputWrappers.forEach((option: HTMLElement, index: number) => {
				if (index === 0) return
				const optionInputNodes = q.ol<
					HTMLInputElement | HTMLOptionElement
				>('input[type="radio"], option', option)
				const optionInputs = optionInputNodes?.length
					? Array.from(optionInputNodes)
					: []

				const previousOptionSelected = q.rs<
					HTMLInputElement | HTMLOptionElement
				>(':checked', inputWrappers[index - 1]).value
				const availableOptionInputsValue = selectedOptionOneVariants
					.filter(
						(variant) =>
							variant.available &&
							this.getVariantOptionFromIndex(variant, index) ===
								previousOptionSelected
					)
					.map((variantOption) =>
						this.getVariantOptionFromIndex(variantOption, index + 1)
					)
				this.setInputAvailability(
					optionInputs,
					availableOptionInputsValue
				)
			})
		} else {
			console.warn('no input wrappers found')
		}
	}

	setInputAvailability(
		listOfOptions: (HTMLInputElement | HTMLOptionElement)[],
		listOfAvailableOptions: (string | null)[]
	) {
		listOfOptions.forEach((input) => {
			if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
				input.innerText = q.ra(input, 'value')
			} else {
				input.innerText =
					window.variantStrings.unavailable_with_option.replace(
						'[value]',
						q.ra(input, 'value')
					)
			}
		})
	}

	updatePickupAvailability() {
		const pickUpAvailability = q.os<PickupAvailability>(
			'pickup-availability'
		)
		if (!pickUpAvailability) return
		const currentVariant = this.currentVariant

		if (currentVariant && currentVariant.available) {
			pickUpAvailability.fetchAvailability(`${currentVariant.id}`)
		} else {
			pickUpAvailability.removeAttribute('available')
			pickUpAvailability.innerHTML = ''
		}
	}

	removeErrorMessage() {
		const section = this.closest('section')
		if (!section) return

		const productForm = q.os<ProductForm>('product-form', section)
		if (productForm) productForm.handleErrorMessage()
	}

	renderProductInfo() {
		if (!this.currentVariant)
			throw new Error(
				'this.currentVariant not selected, renderProductInfo called too early'
			)
		const requestedVariantId = this.currentVariant.id
		const sectionId = this.dataset.originalSection
			? this.dataset.originalSection
			: q.ra(this, 'data-section')

		fetch(
			`${this.dataset.url}?variant=${requestedVariantId}&section_id=${
				this.dataset.originalSection
					? this.dataset.originalSection
					: this.dataset.section
			}`
		)
			.then((response) => response.text())
			.then((responseText) => {
				if (!this.currentVariant)
					throw new Error(
						'this.currentVariant not selected, renderProductInfo called too early'
					)
				// prevent unnecessary ui changes from abandoned selections
				if (this.currentVariant.id !== requestedVariantId) return

				const html = new DOMParser().parseFromString(
					responseText,
					'text/html'
				)
				const sourceSectionId = this.dataset.originalSection
					? this.dataset.originalSection
					: this.dataset.section

				// Update all price elements (main product + sticky ATC)
				const priceSource = html.getElementById(
					`price-${sourceSectionId}`
				)
				const priceDestinations =
					document.querySelectorAll<HTMLElement>(
						`[data-price-update="${this.dataset.section}"]`
					)
				if (priceSource) {
					priceDestinations.forEach((destination) => {
						destination.innerHTML = priceSource.innerHTML
						destination.classList.remove('visibility-hidden')
					})
				}

				const skuSource = html.getElementById(`Sku-${sourceSectionId}`)
				const skuDestination = document.getElementById(
					`Sku-${this.dataset.section}`
				)
				const inventorySource = q.os(
					`#Inventory-${sourceSectionId}`,
					html
				)
				const inventoryDestination = document.getElementById(
					`Inventory-${this.dataset.section}`
				)

				if (inventorySource && inventoryDestination)
					inventoryDestination.innerHTML = inventorySource.innerHTML
				if (skuSource && skuDestination) {
					skuDestination.innerHTML = skuSource.innerHTML
					skuDestination.classList.toggle(
						'visibility-hidden',
						skuSource.classList.contains('visibility-hidden')
					)
				}

				if (inventoryDestination)
					inventoryDestination.classList.toggle(
						'visibility-hidden',
						inventorySource?.innerText === ''
					)

				// Get the source button from fetched HTML
				const addButtonUpdated = html.getElementById(
					`ProductSubmitButton-${sectionId}`
				)
				const isButtonDisabled = addButtonUpdated
					? addButtonUpdated.hasAttribute('disabled')
					: true

				// Update main form button first (original behavior)
				this.toggleAddButton(
					isButtonDisabled,
					window.variantStrings.soldOut
				)

				// Update all submit buttons (main product + sticky ATC) via data attributes
				const ctaSource = html.getElementById(
					`ProductSubmitButton-${sourceSectionId}`
				)
				const ctaDestinations = document.querySelectorAll<HTMLElement>(
					`[data-submit-button-update="${this.dataset.section}"]`
				)

				if (ctaSource && ctaDestinations.length > 0) {
					const sourceDataOosPopupTrigger = q.oa(
						ctaSource,
						'data-oos-popup-trigger'
					)

					ctaDestinations.forEach((ctaDestination) => {
						ctaDestination.innerHTML = ctaSource.innerHTML

						// Update disabled state based on source
						if (isButtonDisabled) {
							ctaDestination.setAttribute('disabled', 'disabled')
							ctaDestination.setAttribute('aria-disabled', 'true')
						} else {
							ctaDestination.removeAttribute('disabled')
							ctaDestination.removeAttribute('aria-disabled')
						}

						// Update OOS popup trigger
						if (sourceDataOosPopupTrigger) {
							ctaDestination.setAttribute(
								'data-oos-popup-trigger',
								sourceDataOosPopupTrigger
							)
						} else {
							ctaDestination.removeAttribute(
								'data-oos-popup-trigger'
							)
						}

						if (
							ctaDestination.hasAttribute('data-sticky-notify') &&
							isButtonDisabled &&
							this.currentVariant &&
							!this.currentVariant.available
						) {
							const textSpan = q.os(
								'[data-uc-inventory-status-text]',
								ctaDestination
							)
							if (textSpan) {
								textSpan.innerHTML =
									'<span class="max-lg:hidden">Notify Me When Available</span>' +
									'<span class="lg:hidden">Notify Me</span>'
							}
						}
					})
				}

				// Update soldout state based on variant availability
				this.updateSoldoutState(!this.currentVariant.available)

				publish(
					PUB_SUB_EVENTS.variantChange,
					createVariantChangeEvent({
						source: 'product-form',
						data: {
							sectionId,
							html,
							variant: this.currentVariant,
						},
					})
				)

				// Reset syncing flag after publishing
				this.isSyncing = false
			})
	}

	toggleAddButton(disable = true, text: string, modifyClass = true) {
		const productForm = q.rs<HTMLFormElement>(
			`#product-form-${this.dataset.section}`
		)
		if (!productForm) return
		const addButton = q.os('[name="add"]', productForm)
		const addButtonText = q.os('[name="add"] > span', productForm)
		if (!addButton || !addButtonText) return

		if (disable) {
			addButton.setAttribute('disabled', 'disabled')
			if (text) addButtonText.textContent = text
		} else {
			addButton.removeAttribute('disabled')
			addButtonText.textContent = window.variantStrings.addToCart
		}

		if (!modifyClass) return
	}

	setUnavailable() {
		const button = q.rs(`#product-form-${this.dataset.section}`)
		const addButton = q.os('[name="add"]', button)
		const addButtonText = q.os('[name="add"] > span', button)
		const inventory = document.getElementById(
			`Inventory-${this.dataset.section}`
		)
		const sku = document.getElementById(`Sku-${this.dataset.section}`)

		if (!addButton || !addButtonText) return
		addButtonText.textContent = window.variantStrings.unavailable

		// Hide all price elements (main product + sticky ATC)
		const priceElements = document.querySelectorAll<HTMLElement>(
			`[data-price-update="${this.dataset.section}"]`
		)
		priceElements.forEach((price) =>
			price.classList.add('visibility-hidden')
		)

		// Update all submit buttons to unavailable state
		const submitButtons = document.querySelectorAll<HTMLElement>(
			`[data-submit-button-update="${this.dataset.section}"]`
		)
		submitButtons.forEach((btn) => {
			btn.setAttribute('disabled', 'disabled')
			btn.setAttribute('aria-disabled', 'true')
			const btnText = q.os('span', btn)
			if (btnText) {
				if (btn.hasAttribute('data-sticky-notify')) {
					btnText.innerHTML =
						'<span class="max-lg:hidden">Notify Me When Available</span>' +
						'<span class="lg:hidden">Notify Me</span>'
				} else {
					btnText.textContent = window.variantStrings.unavailable
				}
			}
		})

		if (inventory) inventory.classList.add('visibility-hidden')
		if (sku) sku.classList.add('visibility-hidden')
	}

	getVariantData(): ProductVariant[] {
		const textContent = q.rs<HTMLScriptElement>(
			'[type="application/json"]',
			this
		).textContent
		if (!textContent) throw new Error('Variant data not found')
		const updatedVariantData = this.variantData ?? JSON.parse(textContent)
		this.variantData = updatedVariantData
		return updatedVariantData
	}

	// Helper function to check if ANY Sculpt 9 product (subscription OR one-time) exists in cart
	async hasSculpt9InCart() {
		try {
			const response = await fetch('/cart.js');
			const cart = await response.json();
			const cartItems = cart?.items || [];

			return cartItems.some((item: any) =>
				item.handle === 'sculpt-9' ||
				item.product_handle === 'sculpt-9' ||
				item.sku === 'SUPPVLSCULPT9' ||
				item.sku === 'SUPPVLSCULPT9-PO'
			);
		} catch (error) {
			console.error('Error checking cart for Sculpt 9:', error);
			return false;
		}
	}

	async updateSubscriptionButtonState() {
		const productForm = q.rs<HTMLFormElement>(
			`#product-form-sticky-${this.dataset.section}`
		)
		if (!productForm) return
		const submitButton = q.os<HTMLInputElement>(
			'button[name="add"]',
			productForm
		)
		if (!submitButton) return

		const hasSculpt9 = await this.hasSculpt9InCart()

		if (hasSculpt9) {
			submitButton.disabled = true
			submitButton.firstElementChild!.textContent = 'Already in Cart'
			submitButton.classList.add('opacity-50', 'cursor-not-allowed')
			submitButton.classList.remove('bg-black')
			submitButton.classList.add('bg-gray-400')
		} else {
			submitButton.disabled = false
			submitButton.firstElementChild!.textContent = 'Add to Cart'
			submitButton.classList.remove('opacity-50', 'cursor-not-allowed', 'bg-gray-400')
			submitButton.classList.add('bg-black')
		}
	}
}
